import axios from "axios";
import { API_BASE_URL } from "../../config";

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
  "Content-Type": "application/json",
});

/**
 * Fetch all email templates
 */
export const fetchEmailTemplates = async (params = {}) => {
  /* The API filters by context (lead / customer), search text, status and type —
     so none of that has to be done after the fact on a partial list. */
  const response = await axios.get(
    `${API_BASE_URL}/get-email-template-list`,
    { params, headers: getAuthHeaders() }
  );
  if (response.data.status) return response.data.data;
  throw new Error(response.data.message || "Failed to fetch email templates");
};

/**
 * Fetch placeholder list for email templates
 */
export const fetchEmailTemplatePlaceholders = async () => {
  const response = await axios.get(
    `${API_BASE_URL}/get-email-template-placeholder-list`,
    { headers: getAuthHeaders() }
  );
  if (response.data.status) return response.data.data;
  throw new Error(response.data.message || "Failed to fetch placeholders");
};

/**
 * Send email using a template
 * @param {Object} payload - { template_id, to: string[], cc?: string[], placeholders: {} }
 */
export const sendEmailUsingTemplate = async (payload) => {
  const loggedInUser = JSON.parse(localStorage.getItem("trade_user_data") || "{}");
  const from_email = loggedInUser?.email || "";

  const { files, existing_attachments, ...rest } = { from_email, ...payload };

  const hasFiles = Array.isArray(files) && files.length > 0 && files[0] instanceof File;
  // const hasExisting = !!existing_attachments;

  if (hasFiles) {
    const fd = new FormData();

    // Append all non-file fields
    Object.entries(rest).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") return;
      if (Array.isArray(value)) {
        value.forEach((item, idx) => {
          fd.append(`${key}[${idx}]`, typeof item === "object" ? JSON.stringify(item) : item);
        });
      } else if (typeof value === "object") {
        fd.append(key, JSON.stringify(value));
      } else {
        fd.append(key, value);
      }
    });

    // User-added File objects
    let attachIdx = 0;
    files.forEach((file) => {
      fd.append(`attachments[${attachIdx}]`, file, file.name);
      attachIdx++;
    });

    // Template existing attachments — commented out for now
    // if (hasExisting) {
    //   fd.append("existing_attachments", existing_attachments);
    // }

    const token = localStorage.getItem("cms_token");
    const res = await axios.post(`${API_BASE_URL}/send-email-using-template-v2`, fd, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  }

  // No files at all — plain JSON
  const token = localStorage.getItem("cms_token");
  const res = await axios.post(`${API_BASE_URL}/send-email-using-template-v2`, rest, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  return res.data;
};

// ─── Lead Email Conversation API ──────────────────────────────────────────────

export const getLeadUnreadCount = async () => {
  const internalUserId = Number(localStorage.getItem("trade_user_id"));
  const res = await axios.get(`${API_BASE_URL}/lead-inbox/unread-count`, {
    params: { internal_user_id: internalUserId },
    headers: getAuthHeaders(),
  });
  return res.data;
};

export const getLeadConversations = async (leadId) => {
  const internalUserId = Number(localStorage.getItem("trade_user_id"));
  const res = await axios.get(`${API_BASE_URL}/lead-inbox/conversations/${leadId}`, {
    params: { internal_user_id: internalUserId },
    headers: getAuthHeaders(),
  });
  const d = res.data;
  // Handle: {data:{conversations:[]}}, {data:[]}, {conversations:[]}, or [] directly
  const raw = d?.data?.conversations ?? d?.data ?? d?.conversations ?? d;
  return Array.isArray(raw) ? raw : [];
};

export const getEmailThread = async (leadId, conversationId) => {
  const internalUserId = Number(localStorage.getItem("trade_user_id"));
  const res = await axios.get(
    `${API_BASE_URL}/lead-inbox/thread/${leadId}`,
    {
      params: { conversation_id: conversationId, internal_user_id: internalUserId },
      headers: getAuthHeaders(),
    }
  );
  const raw = res.data?.data ?? res.data?.messages ?? res.data;
  return Array.isArray(raw) ? raw : [];
};

const _buildEmailFd = (payload) => {
  const { files, cc, bcc, ...rest } = payload;
  const fd = new FormData();
  Object.entries(rest).forEach(([k, v]) => {
    if (v == null || v === "") return;
    fd.append(k, v);
  });
  if (cc?.length) cc.forEach((e, i) => fd.append(`cc[${i}]`, e));
  if (bcc?.length) bcc.forEach((e, i) => fd.append(`bcc[${i}]`, e));
  if (Array.isArray(files)) files.forEach((f, i) => fd.append(`attachments[${i}]`, f, f.name));
  return fd;
};

export const sendLeadEmail = async (payload) => {
  const token = localStorage.getItem("cms_token");
  const hasFiles = Array.isArray(payload.files) && payload.files.length > 0;
  if (hasFiles) {
    const res = await axios.post(`${API_BASE_URL}/lead-inbox/send`, _buildEmailFd(payload), {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  }
  const { files, cc, bcc, ...rest } = payload;
  const body = { ...rest };
  if (cc?.length) body.cc = cc;
  if (bcc?.length) body.bcc = bcc;
  const res = await axios.post(`${API_BASE_URL}/lead-inbox/send`, body, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return res.data;
};

export const sendLeadEmailWithTemplate = async (payload) => {
  const token = localStorage.getItem("cms_token");
  const hasFiles = Array.isArray(payload.files) && payload.files.length > 0;
  if (hasFiles) {
    const res = await axios.post(`${API_BASE_URL}/lead-inbox/send-using-template`, _buildEmailFd(payload), {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  }
  const { files, cc, bcc, ...rest } = payload;
  const body = { ...rest };
  if (cc?.length) body.cc = cc;
  if (bcc?.length) body.bcc = bcc;
  const res = await axios.post(`${API_BASE_URL}/lead-inbox/send-using-template`, body, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return res.data;
};

export const markEmailsRead = async (leadId, conversationId) => {
  const token = localStorage.getItem("cms_token");
  const internalUserId = Number(localStorage.getItem("trade_user_id"));
  const body = { internal_user_id: internalUserId };
  if (conversationId) body.conversation_id = conversationId;
  const res = await axios.patch(`${API_BASE_URL}/lead-inbox/mark-read/${leadId}`, body, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return res.data;
};

// export const sendEmailUsingTemplate = async (payload) => {
//   const { files, ...rest } = payload;
//   const hasFiles = Array.isArray(files) && files.length > 0 && files[0] instanceof File;

//   let body;
//   let reqHeaders;

//   if (hasFiles) {
//     const fd = new FormData();
//     Object.entries(rest).forEach(([k, v]) => {
//       if (v === null || v === undefined) return;
//       if (Array.isArray(v)) {
//         v.forEach(item => fd.append(`${k}[]`, item));
//       } else if (typeof v === "object") {
//         Object.entries(v).forEach(([subKey, subVal]) => {
//           if (subVal !== null && subVal !== undefined) fd.append(`${k}[${subKey}]`, subVal);
//         });
//       } else {
//         fd.append(k, v);
//       }
//     });
//     files.forEach((file, idx) => fd.append(`attachments[${idx}]`, file, file.name));
//     body = fd;
//     reqHeaders = { Authorization: `Bearer ${localStorage.getItem("cms_token")}` };
//   } else {
//     body = rest;
//     reqHeaders = getAuthHeaders();
//   }

//   const response = await axios.post(
//     `${API_BASE_URL}/send-email-using-template-v2`,
//     body,
//     { headers: reqHeaders }
//   );
//   if (response.status === 200) {
//     return response.data || { status: true, message: "Email sent successfully" };
//   }
//   throw new Error(response.data?.message || "Failed to send email");
// };